import { FC, useEffect, useRef, useState } from 'react';
import { Select as AntdSelect, Button, Form, Modal, Radio, Typography } from 'antd';
import styled from 'styled-components';
import { useNotificationContext } from '@/features/notifications';
import { useInviteOrganizationMember } from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { Col, Row } from '@/shared/ui';
import { emailPattern } from '@/shared/utils';
import { captureError } from '@/shared/utils/error-reporting';

type Props = {
  open: boolean;
  onCancel: () => void;
  refetchInvitations: () => void;
};

const { Title } = Typography;

export const InviteMembersModal: FC<Props> = ({
  open,
  onCancel,
  refetchInvitations,
}) => {
  const { theme } = useThemeContext();
  const { activeOrganization } = useRouterContext();
  const { showNotification } = useNotificationContext();
  const [form] = Form.useForm();
  const selectRef = useRef<any>(null);

  const [error, setError] = useState(false);

  const emails = Form.useWatch('emails', form);
  const role = Form.useWatch('role', form);

  const { mutate: inviteMember, isLoading: inviteMemberLoading } =
    useInviteOrganizationMember();

  const handleMemberInvite = (data: any) => {
    const emails = data.emails.map((email: string) =>
      email.trim().toLowerCase(),
    );
    try {
      inviteMember(
        {
          pathParams: { id: activeOrganization?.id! },
          body: {
            emails,
            role: data.role
          },
        },
        {
          onSuccess() {
            refetchInvitations();
            onCancel();
          },
          onError(error: any) {
            if (error.stack.message) {
              showNotification({
                type: 'error',
                message: 'Invitation error',
                description: error.stack.message,
              });

              captureError(error, {
                module: 'invite_members_modal_on_error_invite_member',
                action: 'invite_member',
              });
            }
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'invite_members_modal_on_error_invite_member',
        action: 'invite_member',
      });
    }
  };

  const validateEmails = (emails: string[]) => {
    setError(false);
    const invalidEmails = emails
      .map((email) => email.trim())
      .filter((email) => emailPattern.test(email) === false);

    if (invalidEmails.length) {
      setError(true);
    }
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setError(false);
    } else {
      setTimeout(() => {
        selectRef.current?.focus();
      });
    }
  }, [open]);

  return (
    <Modal
      title={
        <Col
          alignItems="flex-start"
          style={{ margin: '10px 0', width: '100%' }}
        >
          <Title level={4} style={{ lineHeight: 1, marginTop: 15 }}>
            Invite to organization
          </Title>
        </Col>
      }
      destroyOnClose
      forceRender
      width={350}
      open={open}
      footer={null}
      style={{ maxWidth: '90%' }}
      onCancel={onCancel}
    >
      <Form
        name="inviteMemberForm"
        form={form}
        initialValues={{
          remember: true,
          role: 'admin', // Default to admin
        }}
        onFinish={handleMemberInvite}
        layout="vertical"
      >
        <Form.Item
          name="emails"
          label="Add users emails"
          extra={
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                paddingTop: 10,
                color: themeConfig[theme].colors.text,
                opacity: 0.6,
              }}
            >
              Separate multiple emails with commas, semicolons, spaces or just
              hit enter key.
            </div>
          }
          style={{ marginTop: 20 }}
        >
          <Select
            ref={selectRef}
            mode="tags"
            suffixIcon={null}
            style={{ width: '100%' }}
            open={false}
            status={error ? 'error' : undefined}
            onChange={(value) => validateEmails(value as string[])}
            tokenSeparators={[',', ';', ' ']}
          />
        </Form.Item>
        {error && (
          <div style={{ marginTop: -20, fontSize: 12, color: '#ff7875' }}>
            One or more emails you entered are not valid.
          </div>
        )}

        <Form.Item
          name="role"
          label="Role"
          style={{ marginTop: 20 }}
          extra={
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                paddingTop: 10,
                color: themeConfig[theme].colors.text,
                opacity: 0.6,
              }}
            >
            </div>
          }
        >
          <Radio.Group>
            <Radio value='admin'>Admin</Radio>
            <Radio value='reader'>Reader</Radio>
          </Radio.Group>
        </Form.Item>
        <Row>
          <Button
            onClick={onCancel}
            style={{ width: '40%', height: 40, marginTop: 15 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={inviteMemberLoading}
            disabled={error || inviteMemberLoading || !emails?.length}
            style={{ width: '60%', height: 40, marginTop: 15 }}
          >
            Invite users
          </Button>
        </Row>
      </Form>
    </Modal>
  );
};

const Select = styled(AntdSelect)`
  input {
    text-transform: lowercase;
  }
`;
